import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Button, Card, Table, Space, Modal, Row, Col, Tooltip, Input, Tag, Form, Select } from 'antd';
import { EditOutlined, DeleteOutlined, ReloadOutlined, FilterOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { isEmpty, debounce } from 'underscore';

import { AppDispatch, CombinedStates } from '../../core/types';
import { getUsers, createUser, deleteUser } from '../../core/user/userApi';
import { loadSingleUser, resetUserCreated } from '../../core/user/userSlice';
import { getSchools } from '../../core/school/schoolApi';
import { roleOptions } from '../../core/utils/helpers';

import './style.scss';

interface UserListingTableProps {
    showCreateButton?: boolean;
    showFilters?: boolean;
    showSearch?: boolean;
    showReloadButton?: boolean;
    title?: string;
    className?: string;
    customFilters?: any;
    readOnly?: boolean;
}

const UserListingTable: React.FC<UserListingTableProps> = ({
    showCreateButton = true,
    showFilters = true,
    showSearch = true,
    showReloadButton = true,
    title = 'Vartotojai',
    className = '',
    customFilters = {},
    readOnly = false,
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [isFilterVisible, setIsFilterVisible] = useState<boolean>(false);
    const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
    const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
    const [createForm] = Form.useForm();
    const [filters, setFilters] = useState({
        roles: [] as number[],
        schools: [] as string[],
        ...customFilters,
    });

    const {
        users,
        isFetching,
        userData,
        isUserCreated,
    } = useSelector((state: CombinedStates) => state.user);

    // Override readOnly for specific roles
    const effectiveReadOnly = readOnly || userData?.role_id === 3 || userData?.role_id === 4;

    const {
        schools,
        isFetching: isFetchingSchools,
    } = useSelector((state: CombinedStates) => state.school);

    const filteredRoles = roleOptions.filter(role => {
        if (!userData) return false;
        if (userData.role_id === 1) return true; // Super Admin
        if (userData.role_id === 2) return role.id !== 1; // Admin cannot create Super Admin
        if (userData.role_id === 3) return [3, 5].includes(role.id); // Švietimo centras
        if (userData.role_id === 4) return [4, 5].includes(role.id); // Švietimo skyrius
        if (userData.role_id === 5) return role.id === 5; // Mokykla
        return false;
    });

    const debouncedSearch = useCallback(
        debounce((value: string) => {
            setDebouncedSearchTerm(value);
        }, 1000),
        [],
    );

    // Local filtering using useMemo for better performance
    const filteredUsers = useMemo(() => {
        let filtered = users;

        // Apply search filter using debounced term
        if (debouncedSearchTerm.trim()) {
            const searchLower = debouncedSearchTerm.toLowerCase();
            filtered = filtered.filter(user => `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchLower)
                || user.email.toLowerCase().includes(searchLower));
        }

        // Apply role filter
        if (filters.roles.length > 0) {
            filtered = filtered.filter(user => filters.roles.includes(user.role_id));
        }

        // Apply school filter (from manual selection)
        if (filters.schools.length > 0) {
            filtered = filtered.filter(user => user.school_id && filters.schools.includes(Number(user.school_id)));
        }

        // Apply custom school filter (when viewing from school context)
        if (customFilters.school_id) {
            filtered = filtered.filter(user => user.school_id === Number(customFilters.school_id));
        }

        return filtered;
    }, [users, debouncedSearchTerm, filters, customFilters]);

    // Count school users when customFilters has school_id
    const schoolUsersCount = useMemo(() => {
        if (customFilters.school_id) {
            return users.filter(user => user.school_id === Number(customFilters.school_id)).length;
        }
        return 0;
    }, [users, customFilters.school_id]);

    // Check if school user limit is reached
    const isSchoolUserLimitReached = customFilters.school_id && schoolUsersCount >= 2;

    // Trigger fetch on component mount
    useEffect(() => {
        if (isEmpty(users)) {
            dispatch(getUsers(customFilters));
        }
    }, [customFilters]);

    useEffect(() => {
        if (isEmpty(schools)) {
            dispatch(getSchools({}));
        }
    }, []);

    useEffect(() => {
        if (isUserCreated) {
            setIsCreateModalVisible(false);
            createForm.resetFields();
            setSelectedRoleId(null);
            dispatch(resetUserCreated());
        }
    }, [isUserCreated]);

    const doSearch = (term: string) => {
        setSearchTerm(term);
        debouncedSearch(term);
    };

    const handleEdit = (id: number) => {
        dispatch(loadSingleUser(id));
        navigate('/vartotojas/redagavimas');
    };

    const handleDelete = (id: number) => {
        Modal.confirm({
            title: 'Patvirtinkite veiksmą',
            content: 'Ar tikrai norite ištrinti šį vartotoją?',
            okText: 'Taip, ištrinti',
            cancelText: 'Atšaukti',
            okType: 'danger',
            onOk() {
                dispatch(deleteUser({ id }));
            },
        });
    };

    const handleReload = () => {
        dispatch(getUsers(customFilters));
        setSearchTerm('');
        setDebouncedSearchTerm(''); // Reset debounced search term
        setFilters({
            roles: [],
            schools: [],
            ...customFilters,
        });
    };

    const handleFilter = () => {
        setIsFilterVisible(false);
    };

    const handleFilterReset = () => {
        setFilters({
            roles: [],
            schools: [],
            ...customFilters,
        });
    };

    const columns = [
        {
            title: 'Vardas pavardė',
            key: 'full_name',
            render: (_: any, record: any) => `${record.first_name} ${record.last_name}`,
        },
        {
            title: 'El. paštas',
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: 'Telefonas',
            dataIndex: 'phone',
            key: 'phone',
        },
        {
            title: 'Rolė',
            dataIndex: 'role_id',
            key: 'role_id',
            render: (role_id: number) => {
                const role = roleOptions.find(r => r.id === role_id);
                return role ? <Tag color={role.color}>{role.name}</Tag> : <Tag>Nežinoma</Tag>;
            },
        },
        {
            title: 'Mokykla',
            key: 'school',
            render: (_: any, record: any) => {
                if (record.role_id === 5) {
                    const school = schools.find(s => s.id === record.school_id);
                    return school ? <Tag>{school.name}</Tag> : <i>Nepriskirta</i>;
                }
                return '----------------------------'; // only school-level users
            },
        },
        {
            title: 'Sukurta',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (text: string) => new Date(text).toLocaleString('lt-LT'),
        },
        {
            title: 'Atnaujinta',
            dataIndex: 'updated_at',
            key: 'updated_at',
            render: (text: string) => new Date(text).toLocaleString('lt-LT'),
        },
    ];

    // Add actions column only if not read-only
    if (!effectiveReadOnly) {
        columns.push({
            title: 'Veiksmai',
            key: 'actions',
            render: (_: any, record: any) => (
                <Space>
                    <Button icon={<EditOutlined />} onClick={() => handleEdit(record.id)}>
                        Redaguoti
                    </Button>
                    <Button icon={<DeleteOutlined />} danger onClick={() => handleDelete(record.id)}>
                        Pašalinti
                    </Button>
                </Space>
            ),
        });
    }

    return (
        <div className={`user-listing-table ${className}`}>
            <Card style={{ marginTop: '20px' }}>
                <Row className="listing-table-title" justify="space-between" align="middle">
                    <Col>
                        <h3 style={{ marginBottom: 0 }}>{title}</h3>
                    </Col>
                    <Col>
                        <Space>
                            {showFilters && (
                                <Button
                                    icon={<FilterOutlined />}
                                    onClick={() => setIsFilterVisible(true)}
                                    type={filters.roles.length > 0 || filters.schools.length > 0 ? 'primary' : 'default'}>
                                    Filtras
                                    {(filters.roles.length > 0 || filters.schools.length > 0) && (
                                        <span style={{ marginLeft: 4, fontSize: '12px' }}>
                                            ({filters.roles.length + filters.schools.length})
                                        </span>
                                    )}
                                </Button>
                            )}
                            {showSearch && (
                                <Input
                                    placeholder={`Paieška... (${filteredUsers.length}/${users.length})`}
                                    value={searchTerm}
                                    onChange={(e) => doSearch(e.target.value)} />
                            )}
                            {showReloadButton && (
                                <Tooltip title="Atnaujinti sąrašą">
                                    <Button
                                        icon={<ReloadOutlined />}
                                        onClick={handleReload} />
                                </Tooltip>
                            )}
                            {showCreateButton && !effectiveReadOnly && (
                                isSchoolUserLimitReached ? (
                                    <Tooltip title="Mokyklos gali turėti iki 2 vartotojų">
                                        <Button
                                            loading={isFetching}
                                            icon={<PlusCircleOutlined />}
                                            type="primary"
                                            disabled>
                                            Sukurti naują vartotoją
                                        </Button>
                                    </Tooltip>
                                ) : (
                                    <Button
                                        loading={isFetching}
                                        icon={<PlusCircleOutlined />}
                                        type="primary"
                                        onClick={() => setIsCreateModalVisible(true)}>
                                        Sukurti naują vartotoją
                                    </Button>
                                )
                            )}
                        </Space>
                    </Col>
                </Row>
                <Table
                    dataSource={filteredUsers}
                    loading={isFetching || isFetchingSchools}
                    rowKey="id"
                    columns={columns} />
            </Card>

            {/* Filter Modal */}
            {showFilters && (
                <Modal
                    title="Filtruoti vartotojus"
                    open={isFilterVisible}
                    onCancel={() => setIsFilterVisible(false)}
                    onOk={handleFilter}
                    okText="Uždaryti"
                    cancelText="Atšaukti"
                    footer={[
                        <Button key="reset" onClick={handleFilterReset}>
                            Išvalyti filtrus
                        </Button>,
                        <Button key="cancel" onClick={() => setIsFilterVisible(false)}>
                            Atšaukti
                        </Button>,
                        <Button key="ok" type="primary" onClick={handleFilter}>
                            Uždaryti
                        </Button>,
                    ]}>
                    <Form layout="vertical">
                        <Form.Item label="Rolė">
                            <Select
                                mode="multiple"
                                allowClear
                                placeholder="Pasirinkite rolę"
                                value={filters.roles}
                                onChange={(value) => setFilters({ ...filters, roles: value })}>
                                {roleOptions.map(role => (
                                    <Select.Option key={role.id} value={role.id}>
                                        {role.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>

                        {/* Hide school filter for school users */}
                        {userData?.role_id !== 5 && (
                            <Form.Item label="Mokykla">
                                <Select
                                    mode="multiple"
                                    allowClear
                                    showSearch
                                    placeholder="Pasirinkite mokyklą"
                                    value={filters.schools}
                                    onChange={(value) => setFilters({ ...filters, schools: value })}>
                                    {schools.map(school => (
                                        <Select.Option key={school.id} value={school.id}>
                                            {school.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        )}
                    </Form>
                </Modal>
            )}

            {/* Create User Modal */}
            {showCreateButton && !effectiveReadOnly && !isSchoolUserLimitReached && (
                <Modal
                    title="Naujo vartotojo kūrimas"
                    open={isCreateModalVisible}
                    confirmLoading={isFetching}
                    onCancel={() => setIsCreateModalVisible(false)}
                    onOk={() => {
                        createForm
                            .validateFields()
                            .then((values) => {
                                dispatch(createUser({ ...values, ...customFilters }));
                            })
                            .catch(info => {
                                console.log('Validacijos klaida:', info);
                            });
                    }}
                    okText="Sukurti"
                    cancelText="Atšaukti">
                    <Form form={createForm} layout="vertical">
                        <Form.Item
                            name="email"
                            label="El. paštas"
                            rules={[{ required: true, type: 'email', message: 'Įveskite galiojantį el. paštą' }]}>
                            <Input placeholder="El. paštas" />
                        </Form.Item>
                        <Form.Item
                            name="first_name"
                            label="Vardas"
                            rules={[{ required: true, message: 'Įveskite vardą' }]}>
                            <Input placeholder="Vardas" />
                        </Form.Item>
                        <Form.Item
                            name="last_name"
                            label="Pavardė"
                            rules={[{ required: true, message: 'Įveskite pavardę' }]}>
                            <Input placeholder="Pavardė" />
                        </Form.Item>
                        {userData?.role_id !== 5 && (
                            <Form.Item
                                name="role_id"
                                label="Rolė"
                                rules={[{ required: true, message: 'Pasirinkite rolę' }]}>
                                <Select
                                    options={filteredRoles.map(role => ({ label: role.name, value: role.id }))}
                                    placeholder="Pasirinkite rolę"
                                    onChange={value => setSelectedRoleId(value)} />
                            </Form.Item>
                        )}
                        {selectedRoleId === 5 && userData?.role_id !== 5 && (
                            <Form.Item
                                label="Mokykla"
                                name="school_id"
                                rules={[{ required: true, message: 'Pasirinkite mokyklą.' }]}>
                                <Select
                                    options={schools.map(school => ({
                                        label: school.name,
                                        value: school.id,
                                    }))}
                                    placeholder="Pasirinkite mokyklą" />
                            </Form.Item>
                        )}
                    </Form>
                </Modal>
            )}
        </div>
    );
};

UserListingTable.defaultProps = {
    showCreateButton: true,
    showFilters: true,
    showSearch: true,
    showReloadButton: true,
    title: 'Vartotojai',
    className: '',
    customFilters: {},
    readOnly: false,
};

export default UserListingTable;

UserListingTable.propTypes = {};
